import images from '@assets/images/index'
import {
  AssessmentColumnType as ColumnType,
  useAssessmentTable
} from '@suntex.fim/assessment'
import { Button } from '@components/ui'
import { useAssessmentDropdownFilters } from '@hooks/useAssessmentDropdownFilters'
import { useQuery } from '@tanstack/react-query'
import { ColumnFiltersState } from '@tanstack/react-table'
import {
  AssessmentReportSATrendOptions,
  AssessmentReportSAViewOptions
} from '@utils/Variables'
import { GetAPIparams, useDropdownMenu } from '@utils/assessmentReportUtils'
import { objToLevSelOpts } from '@utils/enumHelper'
import { appSessionStorage } from '@utils/storageUtils'
import { Flex } from 'antd'
import { useEffect, useMemo, useState } from 'react'

import { useLocation } from '@suntex.core/nav'
import {
  Database_Procs_ActivityAggregatedByLevel_Item,
  Database_Procs_TeamActivity_Item,
  TeamId
} from '@suntex.fim/api-client'
import {
  getAssessmentReportData,
  getValidAssessmentOption,
  schoolAssessmentInitialSorting,
  teamAssessmentInitialSorting
} from '@suntex.fim/assessment'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import { EntityLevel, SpecialGradeLevel } from '@suntex.fim/enums'
import { useFramework } from '@suntex.fim/framework'
import { SendMessage } from '@suntex.fim/messages'
import { useAppQueryString } from '@suntex.fim/nav'
import { useOptionalTeamInfo } from '@suntex.fim/team'
import {
  ExportMenu,
  LevelSelect,
  LoaderOverlay,
  SearchInput
} from '@suntex.fim/ui'

import AssessmentReportNavigation from './AssessmentReport/AssessmentReportNavigation'
import {
  columns,
  hidableSelectLabel,
  schoolHidableSelectLabel
} from './AssessmentReport/AssessmentReportStickerActivityTable'
import AssessmentReportSummaryDashboard from './AssessmentReport/AssessmentReportSummaryDashboard'
import FactFluency from './AssessmentReport/FactFluency'
import SelectedRowPopUp from './AssessmentReport/SelectedRowPopUp'
import { handleEnterOrSpacePress } from './LearningCenter/util'

type StickerActivityReportData =
  | Database_Procs_ActivityAggregatedByLevel_Item
  | Database_Procs_TeamActivity_Item

const viewOptions = objToLevSelOpts(AssessmentReportSAViewOptions)

const trendOptions = objToLevSelOpts(AssessmentReportSATrendOptions)

const MIN_STICKER_VALUES = [
  50, 100, 200, 250, 300, 400, 500, 600, 750, 800, 900, 1000, 5000
]
const minStickerOptions = MIN_STICKER_VALUES.map((key) => ({ key, label: key }))

function AssessmentReportStickerActivity() {
  const { api } = useFramework()
  const location = useLocation()
  const {
    stickerActivityView,
    minSticker: queryMinSticker,
    trend: queryTrend,
    isChecked: queryIsChecked,
    grade: queryGrade,
    year: queryYear
  } = useAppQueryString()
  const team = useOptionalTeamInfo()
  const { login } = useAuth(isAuthenticated)
  const isSchool =
    login?.entityLevel === EntityLevel.School &&
    location.pathname.includes('school')
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const selectedView =
    Number(stickerActivityView) ||
    appSessionStorage.stickerActivityView ||
    viewOptions[0]!.key
  const [view, setView] = useState(() =>
    getValidAssessmentOption(viewOptions, selectedView)
  )
  const [trend, setTrend] = useState(() =>
    getValidAssessmentOption(
      trendOptions,
      Number(queryTrend) || trendOptions[0]!.key
    )
  )
  const [isChecked, setIsChecked] = useState(queryIsChecked === 'true' || false)
  const [minSticker, setMinSticker] = useState(() =>
    getValidAssessmentOption(
      minStickerOptions,
      Number(queryMinSticker) || minStickerOptions[0]!.key
    )
  )
  const [visibleThisWeekColumns, setVisibleThisWeekColumns] = useState(false)
  const [sendMessageData, setSendMessageData] = useState<any[]>([])
  const [dropdownFilters, setDropDownFilters] = useAssessmentDropdownFilters()

  const { id, year, grade } = GetAPIparams(isSchool, dropdownFilters)

  const { data: assessmentReportData, isLoading } = useQuery({
    queryKey: ['assessment', 'stickerActivity', id, isSchool, year, grade],
    queryFn: async () => {
      if (isSchool) {
        const response = await api.get_Assessment_Activity({
          id: id!,
          year: year!,
          level: login.entityLevel,
          grade
        })
        return response.activityAggregatedByLevel || []
      } else {
        const response = await api.get_Assessment_ActivityTeam({
          teamId: id as TeamId
        })
        return response.teamStickerCountActivities || []
      }
    },
    enabled: id != null
  })
  const assessmentReport = getAssessmentReportData(assessmentReportData)

  // Update localStorage whenever view changes
  useEffect(() => {
    appSessionStorage.stickerActivityView = view

    appSessionStorage.schoolYear = year || ''
    appSessionStorage.assessmentGrade = grade || SpecialGradeLevel.Kindergarten
  }, [view, year, grade])

  /**
   * open modal for send message and set sendMessageData state value
   */
  const sendMessage = (data: any[]) => {
    setIsModalOpen(true)
    setSendMessageData(data)
  }
  const memoColumns = useMemo<{
    finalColumns: ColumnType<Database_Procs_TeamActivity_Item>[]
    highLightIds: any
  }>(
    () =>
      isSchool
        ? columns({
            view,
            visibleThisWeekColumns,
            setVisibleThisWeekColumns
          })
        : columns({
            view,
            visibleThisWeekColumns,
            setVisibleThisWeekColumns,
            sendMessage
          }),
    [view, visibleThisWeekColumns]
  )

  let trendId = trend ? 'thisWeek' : 'today'
  if (view) {
    trendId = trend ? 'weeklyTrend' : 'dailyTrend'
  }

  useEffect(() => {
    if (isChecked) {
      setColumnFilters([
        {
          id: trendId,
          value: [minSticker, null]
        }
      ])
    } else {
      setColumnFilters([])
    }
  }, [isChecked, minSticker, trend])
  /**
   * Initialize usetable with initial data and
   * Destructure table and Table from useTable hooks
   */
  const { table, Table } = useAssessmentTable<StickerActivityReportData>({
    data: assessmentReport,
    columns:
      memoColumns.finalColumns as ColumnType<StickerActivityReportData>[],
    isLoading,
    horizontalScroll: true,
    hideFirstTwoColumns: !isSchool,
    hideFirstColumns: !isSchool,
    globalFilter: searchQuery,

    initialSorting: isSchool
      ? schoolAssessmentInitialSorting
      : teamAssessmentInitialSorting,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    highLightColumnsById: memoColumns.highLightIds,
    manualState: {
      columnFilters
    },
    changeActions: {
      onColumnFiltersChange: setColumnFilters
    }
  })
  const selectedRow = table.getSelectedRowModel().flatRows

  const isDisabled = !selectedRow.length

  const sendMessageUsers = sendMessageData.length
    ? sendMessageData
    : selectedRow
  const selectedUserOptions = sendMessageUsers.map(
    ({ original }: { original: Database_Procs_TeamActivity_Item }) => ({
      label: original.displayName || original.userId,
      value: original.playerId
    })
  )
  const fileName = isSchool
    ? `${login.schoolName}_stickerActivity`
    : `${team?.name}_stickerActivity`
  const exportColumn = isSchool ? schoolHidableSelectLabel : hidableSelectLabel
  const items = useDropdownMenu({
    table,
    hidableSelectLabel: exportColumn,
    fileName,
    hasColumnChange: isSchool ? ['grade'] : [],
    setIsLoading: setIsExporting
  })
  return (
    <>
      <Flex vertical gap="20px" style={{ position: 'relative' }}>
        {/* Loader overlay when loading and popup not open */}
        <LoaderOverlay isLoading={isLoading || isExporting} />
        <AssessmentReportNavigation
          exportButton={
            <ExportMenu
              ariaLabel="Export report options"
              items={items}
              trigger={['click']}
            />
          }
          filters={
            <Flex gap={14} align="end" wrap>
              <Flex flex="1 1 auto" vertical>
                <p className="dd-label fw-bold text-sm">View</p>
                <LevelSelect
                  value={view}
                  onChange={(newView) => {
                    setView(newView)
                    setSearchQuery('')
                  }}
                  options={viewOptions}
                />
              </Flex>
              <Flex gap={6} flex="1 1 auto" vertical>
                <div className="filter-checkbox">
                  <div
                    className="pretty p-icon p-toggle"
                    role="checkbox"
                    aria-checked={isChecked}
                    tabIndex={0}
                    aria-label="Only show students who earned at least"
                    onKeyDown={(e) =>
                      handleEnterOrSpacePress(e, () =>
                        setIsChecked((prev) => !prev)
                      )
                    }
                  >
                    <input
                      type="checkbox"
                      id="at-least"
                      checked={isChecked}
                      onChange={(e) => setIsChecked(e.target.checked)}
                      aria-checked={isChecked}
                      aria-label="Only show students who earned at least"
                    />
                    <div className="state p-on">
                      <images.CheckboxChecked />
                    </div>
                    <div className="state p-off">
                      <images.Checkbox />
                    </div>
                  </div>
                  <label htmlFor="at-least">
                    Only show students
                    <br /> who earned at least
                  </label>
                </div>
              </Flex>
              <Flex gap={6} flex="1 1 auto" vertical>
                <p className="dd-label fw-bold text-sm" />
                <LevelSelect
                  value={minSticker}
                  onChange={(value) => {
                    setMinSticker(value)
                    setIsChecked(true)
                    setSearchQuery('')
                  }}
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  options={minStickerOptions as any}
                  sticker
                />
              </Flex>
              <Flex gap={6} flex="1 1 auto" vertical>
                <p className="dd-label fw-bold text-sm" />
                <LevelSelect
                  value={trend}
                  onChange={(value) => {
                    setTrend(value)
                    setIsChecked(true)
                    setSearchQuery('')
                  }}
                  options={trendOptions}
                />
              </Flex>
            </Flex>
          }
          dropdownFilters={dropdownFilters}
          setDropDownFilters={setDropDownFilters}
          isSchool={isSchool}
        />
        <Flex className="tiles-container" wrap="wrap">
          <Flex
            vertical
            gap="20px"
            flex="1 1 365px"
            className={'left print-hidden'}
          >
            <FactFluency type="blue" />
          </Flex>
          <Flex className="right" flex="1 1 730px">
            <AssessmentReportSummaryDashboard
              dropdownFilters={dropdownFilters}
              isSchool={isSchool}
            />
          </Flex>
        </Flex>
        {/* result */}
        <div className="main-result">
          {/* Updated toolbar design */}
          <Flex
            justify="space-between"
            align="center"
            className="main-result__toolbar"
            wrap
          >
            <h4 className="title">
              {isSchool ? 'School Teams' : 'Team Players'}
            </h4>

            <Flex gap="10px" justify="space-between" wrap>
              <div className="report-search">
                <SearchInput value={searchQuery} onChange={setSearchQuery} />
              </div>
              {!isSchool && (
                <Button
                  intent="tertiary"
                  size="small"
                  disabled={isDisabled}
                  onClick={() => setIsModalOpen(true)}
                  className="print-hidden"
                  tabIndex={0}
                  aria-disabled={isDisabled}
                  aria-label="Send message to selected users"
                  onKeyDown={(e) => {
                    if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
                      setIsModalOpen(true)
                    }
                  }}
                >
                  SEND MESSAGE
                </Button>
              )}
            </Flex>
          </Flex>
          <Table />
        </div>
      </Flex>
      {!isModalOpen && (
        <SelectedRowPopUp
          selectedRow={selectedRow}
          resetRowSelection={table.resetRowSelection}
          handleSendMessage={() => setIsModalOpen(true)}
        />
      )}
      <SendMessage
        isModalOpen={isModalOpen}
        closeModal={(isSuccess) => {
          setIsModalOpen(false)
          setSendMessageData([])
          if (isSuccess) {
            table.resetRowSelection()
          }
        }}
        selectedUserOptions={selectedUserOptions}
        recipientEntityId={team?.id}
      />
    </>
  )
}

export default AssessmentReportStickerActivity
